import { Component, EventEmitter, Output, ViewChild, OnDestroy, OnInit } from '@angular/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { Dayjs } from 'dayjs';
import { DaterangepickerDirective, LocaleConfig } from 'ngx-daterangepicker-material';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { LocaleConfigDate } from 'src/app/constants/date-config.const';
import { SHOW_PER_PAGE_WEBHOOK } from 'src/app/constants/pagination.const';
import { Router } from '@angular/router';
import { BaseService } from 'src/app/services/base.service';
import { RESEND_WEBHOOK, WEBHOOK_PAYMENT } from 'src/app/constants/endpoint.const';
import { NzMessageService } from 'ng-zorro-antd/message';
import { DateRangeChangeEvent } from 'src/app/models/dashboard.model';

interface StatusItem {
  id: string;
  label: string;
  value: string;
  checked: boolean;
}

interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

interface FilterOrder {
  start_date: string;
  end_date: string;
  search?: string;
  status?: string[];
  limit: number;
  offset: number;
  period: string;
}

interface IWebhookPayment {
  status: string | number;
  created_at: string;
  payment_id: string;
  payment_type: string;
  status_code?: string;
}

interface IWebhookResponse {
  data: {
    items: IWebhookPayment[];
    total_count: number;
    total_pages: number;
    page: number;
  };
}

interface IDateRangePickerEvent {
  startDate: Dayjs;
  endDate: Dayjs;
}

@Component({
  standalone: false,
  selector: 'app-webhook-payment',
  templateUrl: './webhook-payment.component.html',
  styleUrl: './webhook-payment.component.scss'
})
export class WebhookPaymentComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  loading = true;

  status: StatusItem[] = [
    { id: 'success', label: 'Success', value: 'success', checked: false },
    { id: 'failed', label: 'Failed', value: 'failed', checked: false },
    { id: 'pending', label: 'Pending', value: 'pending', checked: false },
  ];

  search = localStorage.getItem('search-webhook') || '';
  private searchSubject = new Subject<string>();

  @ViewChild(DaterangepickerDirective, { static: false })
  pickerDirective!: DaterangepickerDirective;

  @ViewChild('dropdownFilter', { static: false }) dropdownFilter!: NgbDropdown;

  selected: DateRange = {
    startDate: localStorage.getItem('start-date-webhook') ? moment(localStorage.getItem('start-date-webhook')) : moment(),
    endDate: localStorage.getItem('end-date-webhook') ? moment(localStorage.getItem('end-date-webhook')) : moment(),
  };

  ranges: any = {
    Today: [moment().startOf('day'), moment().endOf('day')],
    'Last 7 Days': [moment().subtract(6, 'days').startOf('day'), moment().endOf('day')],
    'Last 1 Month': [moment().subtract(29, 'days').startOf('day'), moment().endOf('day')],
    'This Month': [moment().startOf('month'), moment().endOf('month')],
    'Custom Range': [moment().startOf('day'), moment().endOf('day')],
  };

  invalidDates: moment.Moment[] = [
    moment().add(2, 'days'),
    moment().add(3, 'days'),
    moment().add(5, 'days'),
  ];

  isInvalidDate = (m: moment.Moment): boolean => {
    return this.invalidDates.some((d) => d.isSame(m, 'day'));
  };

  setlocale: LocaleConfig = LocaleConfigDate;

  isDisPrev = true;
  isDisNext = false;
  totalList = 0;
  totalPage = 0;
  perPageOptions: number[] = SHOW_PER_PAGE_WEBHOOK;
  dataWebHook: IWebhookPayment[] = [];
  currentPage = Number(localStorage.getItem('pageWebhook')) || 1;

  filterWebHook: FilterOrder = {
    start_date: localStorage.getItem('start-date-webhook') || moment().format('YYYY-MM-DD'),
    end_date: localStorage.getItem('end-date-webhook') || moment().format('YYYY-MM-DD'),
    search: localStorage.getItem('search-webhook') || '',
    status: localStorage.getItem('status-webhook') ? JSON.parse(localStorage.getItem('status-webhook')!) : ['success', 'failed', 'pending'],
    limit: Number(localStorage.getItem('per-pageWebhook')) || SHOW_PER_PAGE_WEBHOOK[0],
    offset: (Number(localStorage.getItem('pageWebhook')) || 1) > 1 ? (Number(localStorage.getItem('pageWebhook')) - 1) * (Number(localStorage.getItem('per-pageWebhook')) || SHOW_PER_PAGE_WEBHOOK[0]) : 0,
    period: 'custom'
  };

  @Output() filterResult = new EventEmitter<FilterOrder | DateRange | StatusItem[]>();

  constructor(
    private router: Router,
    private baseService: BaseService,
    private messageService: NzMessageService
  ) { }

  ngOnInit(): void {
    if (this.filterWebHook.status) {
      this.status.forEach(s => {
        s.checked = this.filterWebHook.status!.includes(s.value);
      });
    }

    setTimeout(() => {
      this.loading = false;
    }, 1000);

    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.filterWebHook.search = value;
        localStorage.setItem('search-webhook', value);
        this.filterWebHook.offset = 0;
        this.currentPage = 1;
        this.updateLocalStorage('pageWebhook', 1);
        this.filterResult.emit(this.filterWebHook);
        this.getDataList();
      });
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onChangeSearch(value: string): void {
    if (value.length >= 2 || value.length === 0) {
      this.searchSubject.next(value);
    }
  }

  openDatepicker(): void {
    this.pickerDirective.open();
  }

  onChangeDate(e: DateRangeChangeEvent | null): void {
    if (!e) return;
    const { startDate, endDate } = e;

    this.filterWebHook.start_date = startDate
      ? startDate.format('YYYY-MM-DD')
      : this.selected.startDate.format('YYYY-MM-DD');
    this.filterWebHook.end_date = endDate
      ? endDate.format('YYYY-MM-DD')
      : this.selected.endDate.format('YYYY-MM-DD');
    this.filterWebHook.offset = 0;

    localStorage.setItem('start-date-webhook', this.filterWebHook.start_date);
    localStorage.setItem('end-date-webhook', this.filterWebHook.end_date);

    localStorage.setItem('selectedSDate-webhook', JSON.stringify(startDate || this.selected.startDate));
    localStorage.setItem('selectedEDate-webhook', JSON.stringify(endDate || this.selected.endDate));

    this.filterResult.emit(this.filterWebHook);
    this.getDataList();
  }

  resetFilter(): void {
    this.selected = {
      startDate: moment(),
      endDate: moment(),
    };
    this.filterWebHook = {
      ...this.filterWebHook,
      offset: 0,
      limit: 20,
      status: ['success', 'failed', 'pending'],
      search: '',
      start_date: this.selected.startDate.format('YYYY-MM-DD'),
      end_date: this.selected.endDate.format('YYYY-MM-DD')
    };

    this.status.forEach(item => item.checked = false);
    this.search = '';

    localStorage.removeItem('search-webhook');
    localStorage.removeItem('status-webhook');
    localStorage.setItem('start-date-webhook', this.filterWebHook.start_date);
    localStorage.setItem('end-date-webhook', this.filterWebHook.end_date);
    this.updateLocalStorage('pageWebhook', 1);
    this.updateLocalStorage('per-pageWebhook', 20);

    this.currentPage = 1;
    this.updatePaginationState();
    this.filterResult.emit(this.filterWebHook);
    this.getDataList();
  }

  getStatus(event: Event, item: StatusItem): void {
    const target = event.target as HTMLInputElement;
    item.checked = target.checked;

    const checkedStatuses = this.status
      .filter(s => s.checked)
      .map(s => s.value);

    this.filterWebHook.status = checkedStatuses.length > 0 ? checkedStatuses : undefined;

    if (this.filterWebHook.status) {
      localStorage.setItem('status-webhook', JSON.stringify(this.filterWebHook.status));
    } else {
      localStorage.removeItem('status-webhook');
    }

    this.filterResult.emit(this.filterWebHook);
    this.getDataList();
  }

  onPerPageChange(perPage: number): void {
    this.filterWebHook.limit = perPage;
    this.filterWebHook.offset = 0;
    this.updateLocalStorage('per-pageWebhook', perPage);
    this.updateLocalStorage('pageWebhook', 1);
    this.updatePaginationState();
    this.filterResult.emit(this.filterWebHook);
    this.getDataList();
  }

  onPageChange(newPage: number): void {
    this.filterWebHook.offset = (newPage - 1) * this.filterWebHook.limit;
    this.updateLocalStorage('pageWebhook', newPage);
    this.updatePaginationState();
    this.filterResult.emit(this.filterWebHook);
    this.getDataList();
  }

  private updatePaginationState(): void {
    const { offset, limit } = this.filterWebHook;
    const currentPage = (offset / limit) + 1;

    this.isDisPrev = offset === 0;
    this.isDisNext =
      this.totalPage <= 1 ||
      this.totalList <= limit ||
      currentPage >= this.totalPage;
  }

  private updateLocalStorage(key: string, value: number): void {
    localStorage.setItem(key, value.toString());
  }

  onDetail(id: string): void {
    this.router.navigate(['/webhook', id]);
  }

  getDataList(): void {
    this.dropdownFilter?.close();
    this.baseService.getData(WEBHOOK_PAYMENT, this.filterWebHook)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: IWebhookResponse) => {
          this.loading = false;
          this.dataWebHook = res.data.items;
          this.totalList = res.data.total_count;
          this.totalPage = res.data.total_pages;
          this.currentPage = res.data.page;
          this.updatePaginationState();
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  onResend(item: IWebhookPayment): void {
    item.status_code = 'pending';
    const params = { payment_id: item.payment_id };
    this.baseService.postData(RESEND_WEBHOOK, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading = false;
          this.getDataList();
          this.messageService.success('Resend successfully');
        },
        error: () => {
          this.loading = false;
          this.getDataList();
          this.messageService.error('Resend failed');
        }
      });
  }

  getStatusClass(value: string | number): string {
    const statusMap: Record<string | number, string> = {
      'success': 'status-success',
      'failed': 'status-danger',
      'pending': 'status-warning',
      200: 'status-success',
      400: 'status-danger',
      404: 'status-danger'
    };
    return statusMap[value] || 'status-default';
  }

  getStatusValue(value: string | number): string {
    const valueMap: Record<string | number, string> = {
      'success': 'Success',
      'failed': 'Failed',
      'pending': 'Pending',
      200: 'Success',
      400: 'Failed',
      404: 'Failed'
    };
    return valueMap[value] || 'Pending';
  }

  getPaymentValue(value: string): string {
    const paymentMap: Record<string, string> = {
      'bank_transfer': 'Bank Transfer',
      'qris': 'QRIS',
      'va': 'VA'
    };
    return paymentMap[value] || value;
  }
}
